import { JobDecorator } from 'decorators'
import { isObject } from 'lodash'
import Decorator from './decorator'
import moment from 'moment'
import accounting from 'accounting'

export default class InvoiceDecorator extends Decorator {
  /**
   * The company name of the user this invoice belongs to.
   */
  userName () {
    if (isObject(this.user)) {
      return this.decorated.user.profile.company_name
    }

    return '–'
  }

  /**
   * The user's address.
   */
  userAddress () {
    if (isObject(this.user)) {
      return this.decorated.user.profile.address_line_1
    }
  }

  /**
   * The user's suburb.
   */
  userSuburb () {
    if (isObject(this.user)) {
      return this.decorated.user.profile.suburb
    }
  }

  /**
   * The user's state.
   */
  userState () {
    if (isObject(this.user)) {
      return this.decorated.user.profile.state
    }
  }

  /**
   * The user's postcode.
   */
  userPostcode () {
    if (isObject(this.user)) {
      return this.decorated.user.profile.postcode
    }
  }

  /**
   * Total GST being paid.
   */
  totalGst () {
    if (!this.decorated.total_including_gst) {
      return '–'
    }

    return accounting.formatMoney(this.decorated.total_including_gst - this.decorated.total_excluding_gst)
  }

  /**
   * The total cost including GST.
   */
  totalWithGst () {
    if (!this.decorated.total_including_gst) {
      return accounting.formatMoney(0)
    }

    return accounting.formatMoney(this.decorated.total_including_gst)
  }

  /**
   * The total cost of the invoice without GST.
   */
  totalWithoutGst () {
    if (!this.decorated.total_excluding_gst) {
      return '–'
    }

    return accounting.formatMoney(this.decorated.total_excluding_gst)
  }

  /**
   * The amount that has been paid.
   */
  paid () {
    if (!this.decorated.amount_paid) {
      return accounting.formatMoney(0)
    }

    return accounting.formatMoney(this.decorated.amount_paid)
  }

  /**
   * The amount that still needs to be paid.
   */
  remaining () {
    if (!this.decorated.total_including_gst) {
      return accounting.formatMoney(0)
    }

    return accounting.formatMoney(this.decorated.total_including_gst - this.decorated.amount_paid)
  }

  /**
   * The due date.
   *
   * @return string
   */
  dueDate () {
    if (!this.decorated.due_date) {
      return '–'
    }

    return this.decorated.due_date
  }

  /**
   * The due date in an alternative format.
   *
   * @return string
   */
  dueDateAlternative () {
    if (!this.decorated.due_date) {
      return '–'
    }

    return moment(this.decorated.due_date, 'DD/MM/YYYY').format('DD MMMM YYYY')
  }

  /**
   * The created at date.
   *
   * @return string
   */
  createdDate () {
    if (!this.decorated.created_at) {
      return '–'
    }

    return this.decorated.created_at
  }

  /**
   * The created date in an alternative format.
   *
   * @return string
   */
  createdDateAlternative () {
    if (!this.decorated.created_at) {
      return '–'
    }

    return moment(this.decorated.created_at, 'DD/MM/YYYY').format('DD MMMM YYYY')
  }

  /**
   * The order_reference.
   *
   * @return string
   */
  orderReference () {
    if (!this.decorated.order_reference) {
      return '–'
    }

    return this.decorated.order_reference
  }

  /**
   * The date it was paid (Only payments will have this?)
   *
   * @return string
   */
  paidDate () {
    if (!this.decorated.paid_date) {
      return '–'
    }

    return this.decorated.paid_date
  }

  /**
   * The paid date in an alternative format.
   *
   * @return string
   */
  paidDateAlternative () {
    if (!this.decorated.paid_date) {
      return '–'
    }

    return moment(this.decorated.paid_date, 'DD/MM/YYYY').format('DD MMMM YYYY')
  }

  /**
   * Decorated job.
   *
   * @return JobDecorator
   */
  job () {
    if (isObject(this.decorated.job)) {
      return new JobDecorator(this.decorated.job)
    }

    return null
  }

  /**
   * Determine if invoice is paid.
   *
   * @return boolean
   */
  isPaid () {
    return this.decorated.status === 'paid'
  }

  /**
   * Determine if invoice is not paid.
   *
   * @return boolean
   */
  isNotPaid () {
    return this.decorated.status !== 'paid'
  }

  /**
   * Determine if invoice is overdue.
   *
   * @return boolean
   */
  isOverdue () {
    return this.decorated.status === 'overdue'
  }

  /**
   * Determine if invoice is current.
   *
   * @return boolean
   */
  isCurrent () {
    return this.decorated.status === 'current'
  }

  /**
   * Invoice status.
   *
   * @return string
   */
  statusText () {
    if (this.decorated.status === 'paid') {
      return 'Paid'
    } else if (this.decorated.status === 'overdue') {
      return 'Overdue'
    } else if (this.decorated.status === 'current') {
      return 'Current'
    }
  }

  /**
   * Invoice status character letter. (for invoice item)
   *
   * @return string
   */
  statusLetter () {
    return this.status_text.charAt(0)
  }

  /**
   * Invoice status class. (for invoice item)
   *
   * @return string
   */
  statusClass () {
    return `invoice-${this.decorated.status}`
  }

  /**
   * Determine if the payment has some payments against it.
   *
   * @return boolean
   */
  hasSomePayments () {
    return this.decorated.amount_paid > 0
  }
}



// WEBPACK FOOTER //
// ./src/decorators/invoice.js